import { useEffect, useState } from "react"

/** Live media query match; false where matchMedia is missing (tests, old browsers). */
export function useMediaQuery(query: string): boolean {
  const get = () => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : false)
  const [matches, setMatches] = useState(get)
  useEffect(() => {
    const mq = window.matchMedia?.(query)
    if (!mq) return
    const on = () => setMatches(mq.matches)
    on()
    mq.addEventListener?.("change", on)
    return () => mq.removeEventListener?.("change", on)
  }, [query])
  return matches
}

export const useReducedMotion = () => useMediaQuery("(prefers-reduced-motion: reduce)")

/** The one theme preference, for the app and the site. Light unless the reader chose dark. */
export const THEME_KEY = "nira.theme"
/** Where the choice was kept before 19 Oct 2026; read once, so nobody loses theirs. */
const OLD_THEME_KEY = "nira-theme"

/** The reader's choice, or light: a fresh profile is light whatever the OS says. */
export function storedTheme(): "light" | "dark" {
  try {
    const t = window.localStorage.getItem(THEME_KEY) ?? window.localStorage.getItem(OLD_THEME_KEY)
    return t === "dark" ? "dark" : "light"
  } catch {
    return "light"
  }
}

/** Put the theme on <html> (data-theme); called once before the first render, for every route. */
export function applyTheme(): void {
  if (typeof document !== "undefined") document.documentElement.dataset.theme = storedTheme()
}

/** Current theme: the data-theme on <html>, which applyTheme always sets. */
export function isDarkTheme(): boolean {
  return document.documentElement.dataset.theme === "dark"
}

/** Flip light and dark; remembered per browser (storage may be blocked, which is fine). */
export function toggleTheme(): void {
  const next = isDarkTheme() ? "light" : "dark"
  document.documentElement.dataset.theme = next
  try {
    window.localStorage.setItem(THEME_KEY, next)
  } catch {
    /* storage blocked: the attribute still applies for this visit */
  }
}

/** Re-apply the remembered choice on mount (applyTheme already ran at start; this keeps a page
 * that mounts after a choice was made elsewhere in sync). */
export function useStoredTheme(): void {
  useEffect(() => {
    applyTheme()
  }, [])
}

/** Re-render when the theme attribute or OS scheme changes; returns isDarkTheme(). */
export function useIsDark(): boolean {
  const [dark, setDark] = useState(() => (typeof document !== "undefined" ? isDarkTheme() : false))
  useEffect(() => {
    const sync = () => setDark(isDarkTheme())
    const mo = new MutationObserver(sync)
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] })
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)")
    mq?.addEventListener?.("change", sync)
    return () => {
      mo.disconnect()
      mq?.removeEventListener?.("change", sync)
    }
  }, [])
  return dark
}

/** Set the DOM `inert` flag (React 18 has no typed prop for it). */
export function setInert(el: HTMLElement | null, inert: boolean): void {
  if (!el) return
  if (inert) el.setAttribute("inert", "")
  else el.removeAttribute("inert")
}

/** The browser tab's title: "DRC-01, FY 2020-21 · Nira", or "Nira" alone. */
export function usePageTitle(name: string | undefined): void {
  useEffect(() => {
    document.title = name ? `${name} · Nira` : "Nira"
  }, [name])
}
